import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';
import {
  LIVE_QUEST_HERO_ID,
  LIVE_QUEST_LEADERBOARD_PRIZE_ASSETS,
  LIVE_QUEST_PRODUCT_KEY_PREFIX,
  LIVE_QUEST_REWARD_SLOTS,
  LIVE_QUEST_SEASON_ID,
  LIVE_QUEST_SEASON_NAME,
  LIVE_QUEST_UI_ASSETS,
  LiveQuestLeaderboardPrizeAsset,
  LiveQuestRewardSlot,
  LiveQuestTrack,
  LiveQuestUiAsset,
  liveQuestProductKey,
} from '../data/liveQuestRewards';
import { DEFAULT_ONBOARDING_CONTENT, LiveQuestOnboardingContent, parseOnboardingContent } from '../data/liveQuestOnboardingContent';
import {
  CHAT_THEME_DARK_SLOTS,
  CHAT_THEME_MEDIA_TYPES,
  ChatThemeSettings,
  DEFAULT_CHAT_THEME_SETTINGS,
  LiveQuestAssetKind,
  LiveQuestMediaSlot,
  allowedMediaTypes,
  checkLiveQuestFile,
  deriveChatThemeMedia,
  isHexColor,
  liveQuestAssetKind,
  liveQuestAssetProfile,
  liveQuestProductClass,
  liveQuestProductClassForMedia,
  missingRequiredSlots,
} from '../data/liveQuestAssetRules';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;
const MEDIA_TYPES = new Set<string>([...CHAT_THEME_MEDIA_TYPES, 'webm', 'ios_alpha']);
const FALLBACK_CONTENT_TYPE: Record<string, string> = { webm: 'video/webm', ios_alpha: 'video/quicktime' };

// The wallet catalog keeps the legacy PROFILE-without-Type request scoped to profile frames for
// old app clients. The Live Quest admin needs every usable profile class, so ask V2 for each type
// explicitly. This also preserves static background images; the legacy endpoint strips them.
export const LIVE_QUEST_CATALOG_REQUESTS = [
  { CategoryKey: 'LIVE_QUEST', Type: 'utility' },
  { CategoryKey: 'PROFILE', Type: 'chat_themes' },
  { CategoryKey: 'PROFILE', Type: 'background' },
  { CategoryKey: 'PROFILE', Type: 'background_animated' },
  { CategoryKey: 'PROFILE', Type: 'profile_frame' },
  { CategoryKey: 'PROFILE', Type: 'profile_frame_animated' },
  { CategoryKey: 'PROFILE', Type: 'profile_badge' },
  { CategoryKey: 'LIVE', Type: 'entry_animation' },
] as const;

type SavedMedia = { Type: string; Url: string; MimeType?: string; Width?: number; Height?: number };

type SavedProduct = {
  ProductKey: string;
  CategoryKey?: string;
  Type?: string;
  MediaAssets?: SavedMedia[];
  Metadata?: { LiveQuestRewardId?: string; LiveQuestSeasonId?: string; LockAppearance?: boolean; OnboardingContent?: unknown };
  updatedAt?: string;
};

type SavedAsset = SavedMedia & { updatedAt?: string };
type RewardAssets = Partial<Record<string, SavedAsset>>;

type UploadDescription = {
  productKey: string;
  name: string;
  description: string;
  order: number;
  tags: string[];
  metadata: Record<string, unknown>;
};

type Filter = 'all' | 'missing' | 'uploaded' | 'cosmetics';

const rewardIdFromProduct = (product: SavedProduct) => {
  const metadataId = product.Metadata?.LiveQuestRewardId;
  if (metadataId) return metadataId;
  const productKey = product.ProductKey.toUpperCase();
  if (productKey === `${LIVE_QUEST_PRODUCT_KEY_PREFIX}HERO`) return LIVE_QUEST_HERO_ID;
  if (!productKey.startsWith(LIVE_QUEST_PRODUCT_KEY_PREFIX)) return undefined;
  return productKey.slice(LIVE_QUEST_PRODUCT_KEY_PREFIX.length).match(/^(FREE|PLUS)_(\d+)$/)?.slice(1).join('_').toLowerCase();
};

/** Picture shown on the card: the thumbnail (animated rewards, chat themes), else the static image. */
const previewOf = (have: RewardAssets) => have.thumbnail || have.image;

const isComplete = (kind: LiveQuestAssetKind, have: RewardAssets, lockAppearance = true) => {
  const profile = liveQuestAssetProfile(kind);
  // A chat theme also needs its bubble colors (the same in both modes when the look is locked).
  if (kind === 'chat_theme') {
    const requiredProfile = lockAppearance
      ? profile
      : { ...profile, slots: [...profile.slots, ...CHAT_THEME_DARK_SLOTS] };
    return (
      missingRequiredSlots(requiredProfile, have).length === 0 &&
      !!have.sender_bubble_light_mode &&
      !!have.receiver_bubble_light_mode &&
      !!have.sender_bubble_dark_mode &&
      !!have.receiver_bubble_dark_mode
    );
  }
  const hasRequired = profile.slots.some((slot) => slot.required);
  return hasRequired ? missingRequiredSlots(profile, have).length === 0 : !!previewOf(have);
};

const ratioOf = (asset: { Width?: number; Height?: number }) =>
  asset.Width && asset.Height ? asset.Width / asset.Height : null;

/** Frames and animations must keep one shape, otherwise the app scales one of them wrongly. */
const assertSameShape = (kind: LiveQuestAssetKind, next: SavedMedia, others: SavedMedia[]) => {
  if (kind !== 'profile_frame' && kind !== 'profile_frame_animated') return;
  const nextRatio = ratioOf(next);
  if (!nextRatio) return;
  const clash = others.find((other) => {
    const r = ratioOf(other);
    return r && Math.abs(r - nextRatio) / nextRatio > 0.02;
  });
  if (clash) {
    throw new Error(
      `All files must have the same aspect ratio (${next.Type} is ${next.Width}×${next.Height}, ${clash.Type} is ${clash.Width}×${clash.Height}).`
    );
  }
};

const LiveQuestAssets: React.FC = () => {
  const [searchParams] = useSearchParams();
  const savedPackId = searchParams.get('savedPack') || '';
  const requestedTrack = searchParams.get('track') === 'plus' ? 'plus' : 'basic';
  const [track, setTrack] = useState<LiveQuestTrack>(requestedTrack);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState(savedPackId);
  const [assets, setAssets] = useState<Record<string, RewardAssets>>({});
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [productClasses, setProductClasses] = useState<Record<string, { CategoryKey?: string; Type?: string }>>({});
  const [themeLocks, setThemeLocks] = useState<Record<string, boolean>>({});
  const [themeDrafts, setThemeDrafts] = useState<Record<string, ChatThemeSettings>>({});
  const [contentDraft, setContentDraft] = useState<LiveQuestOnboardingContent>(parseOnboardingContent(DEFAULT_ONBOARDING_CONTENT));

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const loadAssets = useCallback(async () => {
    const headers = authHeaders();
    const userId = (localStorage.getItem('wallet_assets_user_id') || '').trim();
    if (!headers || !userId) {
      if (!userId) toast.error('Your account ID is missing. Sign out and back in.');
      return;
    }

    setLoading(true);
    try {
      // Artwork products live in LIVE_QUEST (coins, covers, hero) and, for item rewards that can be
      // given to users, in their real category (PROFILE: themes, backgrounds, frames; LIVE: entry
      // animations). Only the ones tagged with a reward id are Live Quest's.
      const loaded = await Promise.all(
        LIVE_QUEST_CATALOG_REQUESTS.map(async ({ CategoryKey, Type }) => {
          const response = await fetch(`${walletApiBase}/getWalletShopProductsV2`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              UserID: userId,
              CategoryKey,
              Type,
              IncludeAnimation: true,
              IncludeInactive: true,
              Environment: 'prod',
            }),
          });
          const json = await response.json();
          if (!response.ok) throw new Error(json.message || 'Failed to load Live Quest assets');
          return ((json.data?.products || []) as SavedProduct[]).filter(
            (product) =>
              (!!product.Metadata?.LiveQuestRewardId &&
                (product.Metadata.LiveQuestSeasonId === LIVE_QUEST_SEASON_ID ||
                  (LIVE_QUEST_SEASON_ID === 'season-1' && !product.Metadata.LiveQuestSeasonId))) ||
              product.ProductKey.toUpperCase().startsWith(LIVE_QUEST_PRODUCT_KEY_PREFIX)
          );
        })
      );
      const products = loaded.flat();
      setContentDraft(parseOnboardingContent(
        products.find((product) => rewardIdFromProduct(product) === 'onboarding_hero')?.Metadata?.OnboardingContent
      ));

      const next: Record<string, RewardAssets> = {};
      const locks: Record<string, boolean> = {};
      const classes: Record<string, { CategoryKey?: string; Type?: string }> = {};
      products.forEach((product) => {
        const rewardId = rewardIdFromProduct(product);
        if (!rewardId) return;
        classes[rewardId] = { CategoryKey: product.CategoryKey, Type: product.Type };
        if (typeof product.Metadata?.LockAppearance === 'boolean') locks[rewardId] = product.Metadata.LockAppearance;
        (product.MediaAssets || []).forEach((media) => {
          if (!MEDIA_TYPES.has(media.Type) || !media.Url) return;
          next[rewardId] = { ...next[rewardId], [media.Type]: { ...media, updatedAt: product.updatedAt } };
        });
      });
      setAssets(next);
      setThemeLocks(locks);
      setProductClasses(classes);
      setThemeDrafts({});
    } catch (error) {
      toast.error((error as Error).message || 'Failed to load Live Quest assets');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  /** Writes the reward's product with exactly these files (the console owns the full list). */
  const saveProduct = async (
    headers: Record<string, string>,
    rewardId: string,
    kind: LiveQuestAssetKind,
    describe: UploadDescription,
    mediaAssets: SavedMedia[]
  ) => {
    const productClass = liveQuestProductClassForMedia(kind, mediaAssets);
    // The wallet product API still accepts the legacy single-bubble fields for
    // these two existing Plus themes. Their receiver uses the app default, so
    // do not send unsupported sender/receiver media variants on a preference save.
    const productMedia = kind === 'chat_theme' && (rewardId === 'plus_5' || rewardId === 'plus_30')
      ? mediaAssets.filter((media) => !/^(?:sender|receiver)_bubble_(?:light|dark)_mode$/.test(media.Type))
      : mediaAssets;
    const saveResponse = await fetch(`${walletApiBase}/createWalletShopProduct`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: describe.productKey,
        Name: describe.name,
        Description: describe.description,
        // Item rewards are saved with their real type and category so they can be given to users;
        // everything else only holds artwork (LIVE_QUEST utility).
        CategoryKey: productClass.CategoryKey,
        Type: productClass.Type,
        AvailabilityType: 'unlimited',
        CurrencyType: 'coin',
        PriceCoins: 0,
        DisplayOrder: describe.order,
        // Asset records are deliberately inactive so they never appear as free, purchasable
        // utility products in the normal wallet shop. Live Quest reads this category directly.
        IsActive: false,
        Environment: 'prod',
        Tags: describe.tags,
        MediaAssets: productMedia,
        Metadata: describe.metadata,
        // These two Plus themes are dark-only even when their light/dark
        // artwork is deliberately identical. Other locked themes keep their
        // existing light appearance.
        ...(kind === 'chat_theme' ? {
          ThemePreference: rewardId === 'plus_5' || rewardId === 'plus_30'
            ? 'dark'
            : describe.metadata?.LockAppearance === true ? 'light' : 'adaptive',
        } : {}),
      }),
    });
    const saveJson = await saveResponse.json();
    if (!saveResponse.ok) throw new Error(saveJson.message || 'Failed to save the reward artwork');
  };


  const themeSettingsFor = useCallback((rewardId: string): ChatThemeSettings => {
    const draft = themeDrafts[rewardId];
    if (draft) return draft;
    const have = assets[rewardId] || {};
    return {
      lockAppearance: themeLocks[rewardId] ?? DEFAULT_CHAT_THEME_SETTINGS.lockAppearance,
      senderLight: have.sender_bubble_light_mode?.Url || have.bubble_light_mode?.Url || '',
      receiverLight: have.receiver_bubble_light_mode?.Url || '',
      senderDark: have.sender_bubble_dark_mode?.Url || have.bubble_dark_mode?.Url || '',
      receiverDark: have.receiver_bubble_dark_mode?.Url || '',
    };
  }, [assets, themeDrafts, themeLocks]);

  /**
   * Saves the reward's product with this list of files. Files the reward kind does not use are
   * dropped (artwork left from the reward that used to sit at this level can no longer be shown
   * instead of the right one), and a chat theme gets its light/dark entries derived from the
   * theme settings.
   */
  const commitMedia = async (
    headers: Record<string, string>,
    rewardId: string,
    kind: LiveQuestAssetKind,
    describe: UploadDescription,
    media: SavedMedia[],
    settings: ChatThemeSettings = themeSettingsFor(rewardId)
  ) => {
    const allowed = allowedMediaTypes(kind);
    let final = media.filter((item) => allowed.has(item.Type));
    let metadata = describe.metadata;
    if (kind === 'chat_theme') {
      final = deriveChatThemeMedia(final, settings);
      metadata = { ...metadata, LockAppearance: settings.lockAppearance };
    }
    await saveProduct(headers, rewardId, kind, { ...describe, metadata }, final);
    setProductClasses((current) => ({ ...current, [rewardId]: liveQuestProductClassForMedia(kind, final) }));
    const stamped = new Date().toISOString();
    setAssets((current) => ({
      ...current,
      [rewardId]: final.reduce<RewardAssets>((acc, item) => ({ ...acc, [item.Type]: { ...item, updatedAt: stamped } }), {}),
    }));
    setThemeLocks((current) => ({ ...current, [rewardId]: settings.lockAppearance }));
    setThemeDrafts((current) => {
      const { [rewardId]: _dropped, ...rest } = current;
      return rest;
    });
  };

  /** Drops files this kind of reward does not use (left over from the reward that used to be here). */
  const cleanStale = async (reward: LiveQuestRewardSlot) => {
    const headers = authHeaders();
    if (!headers) return;
    setUploadingKey(`${reward.id}:clean`);
    try {
      const media = Object.values(assets[reward.id] || {})
        .filter((item): item is SavedAsset => !!item)
        .map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height }));
      await commitMedia(headers, reward.id, liveQuestAssetKind(reward), describeReward(reward), media);
      toast.success('Old artwork removed');
    } catch (error) {
      toast.error((error as Error).message || 'Could not clean up the artwork');
    } finally {
      setUploadingKey('');
    }
  };

  const saveThemeSettings = async (
    rewardId: string,
    describe: UploadDescription,
    settings: ChatThemeSettings
  ) => {
    const headers = authHeaders();
    if (!headers) return;
    const hexError = (value: string) => value.trim() !== '' && !isHexColor(value);
    if (
      hexError(settings.senderLight) ||
      hexError(settings.receiverLight) ||
      (!settings.lockAppearance && (hexError(settings.senderDark) || hexError(settings.receiverDark)))
    ) {
      toast.error('Bubble colors must be a 6-digit hex code like #F0DBE0.');
      return;
    }
    // The two existing Plus themes were authored with a sender color and the
    // app's default receiver bubble. Preserve that intentional empty value
    // when saving their dark-only preference; do not invent a new color.
    const usesDefaultReceiver = rewardId === 'plus_5' || rewardId === 'plus_30';
    if (!isHexColor(settings.senderLight) ||
        (!usesDefaultReceiver && !isHexColor(settings.receiverLight))) {
      toast.error(usesDefaultReceiver
        ? 'Enter the sender color first.'
        : 'Enter both sender and receiver colors first.');
      return;
    }
    setUploadingKey(`${rewardId}:settings`);
    try {
      // Compare with what is persisted, not the live draft. The draft changes immediately so the
      // dark upload fields can appear before Save is pressed.
      const wasLocked = themeLocks[rewardId] ?? DEFAULT_CHAT_THEME_SETTINGS.lockAppearance;
      let media = Object.values(assets[rewardId] || {}).filter((item): item is SavedAsset => !!item);
      // Unlocking: the dark files were copies of the light ones, so they must be uploaded fresh.
      if (wasLocked && !settings.lockAppearance) {
        media = media.filter((item) => item.Type !== 'theme_dark_mode' && item.Type !== 'thumbnail_dark_mode');
      }
      await commitMedia(
        headers,
        rewardId,
        'chat_theme',
        describe,
        media.map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height })),
        settings
      );
      // Do not rely only on the optimistic card state: reload the product so the fields displayed
      // after Save are exactly what the wallet API persisted.
      await loadAssets();
      toast.success('Theme settings saved');
    } catch (error) {
      toast.error((error as Error).message || 'Could not save the theme settings');
    } finally {
      setUploadingKey('');
    }
  };

  const handleRemove = async (
    rewardId: string,
    kind: LiveQuestAssetKind,
    slot: LiveQuestMediaSlot,
    describe: UploadDescription
  ) => {
    const headers = authHeaders();
    if (!headers) return;
    if (!window.confirm(`Remove the ${slot.label} file from this reward?`)) return;
    const key = `${rewardId}:${slot.type}`;
    setUploadingKey(key);
    try {
      const remaining = Object.values(assets[rewardId] || {})
        .filter((media): media is SavedAsset => !!media && media.Type !== slot.type)
        .map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height }));
      await commitMedia(headers, rewardId, kind, describe, remaining);
      toast.success(`${slot.label} removed`);
    } catch (error) {
      toast.error((error as Error).message || 'Could not remove the file');
    } finally {
      setUploadingKey('');
    }
  };

  const handleUpload = async (
    rewardId: string,
    kind: LiveQuestAssetKind,
    slot: LiveQuestMediaSlot,
    file: File,
    describe: UploadDescription
  ) => {
    const headers = authHeaders();
    if (!headers) return;

    const key = `${rewardId}:${slot.type}`;
    setUploadingKey(key);
    try {
      const size = await checkLiveQuestFile(kind, slot, file);
      const contentType = file.type || FALLBACK_CONTENT_TYPE[slot.type] || 'application/octet-stream';
      const productKey = describe.productKey;

      const presignResponse = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: productKey,
          ContentType: contentType,
          AssetType: slot.type,
          AssetKind: kind,
          FileSize: file.size,
          Width: size.Width,
          Height: size.Height,
        }),
      });
      const presignJson = await presignResponse.json();
      if (!presignResponse.ok) throw new Error(presignJson.message || 'Failed to prepare the upload');

      const { UploadUrl, AssetUrl } = presignJson.data;
      const uploadResponse = await fetch(UploadUrl, {
        method: 'PUT',
        body: await file.arrayBuffer(),
        headers: { 'Content-Type': contentType },
      });
      if (!uploadResponse.ok) throw new Error('Failed to upload the file to storage');

      const uploaded: SavedMedia = { Type: slot.type, Url: AssetUrl, MimeType: contentType, ...size };
      const others = Object.values(assets[rewardId] || {}).filter(
        (media): media is SavedAsset => !!media && media.Type !== slot.type
      );
      assertSameShape(kind, uploaded, others);

      // Keep the files already attached: the product holds every slot of the reward.
      const mediaAssets: SavedMedia[] = [
        ...others.map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height })),
        uploaded,
      ];

      await commitMedia(headers, rewardId, kind, describe, mediaAssets);
      toast.success(`${slot.label} updated`);
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploadingKey('');
    }
  };

  const visibleRewards = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return LIVE_QUEST_REWARD_SLOTS.filter((reward) => reward.track === track)
      .filter((reward) => {
        const kind = liveQuestAssetKind(reward);
        const done = isComplete(kind, assets[reward.id] || {}, themeSettingsFor(reward.id).lockAppearance);
        if (filter === 'missing') return !done;
        if (filter === 'uploaded') return done;
        if (filter === 'cosmetics') return !['pcoins', 'tokens', 'plusDuration'].includes(reward.rewardType);
        return true;
      })
      .filter(
        (reward) =>
          !normalizedQuery ||
          reward.title.toLowerCase().includes(normalizedQuery) ||
          reward.subtitle.toLowerCase().includes(normalizedQuery) ||
          reward.id.includes(normalizedQuery) ||
          `${reward.level}` === normalizedQuery
      );
  }, [assets, filter, query, themeSettingsFor, track]);

  const [syncingPack, setSyncingPack] = useState('');
  const syncStickerPack = async (rewardId: string) => {
    const headers = authHeaders();
    if (!headers) return;
    setSyncingPack(rewardId);
    try {
      const response = await fetch(`${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live/admin/live-quest-sticker-packs/sync`, {
        method: 'POST', headers, body: JSON.stringify({ rewardId }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || 'Could not update chat stickers');
      toast.success(`${json.data.count} stickers are ready in chat for users who claimed this reward`);
    } catch (error) {
      toast.error((error as Error).message || 'Could not update chat stickers');
    } finally {
      setSyncingPack('');
    }
  };

  const doneCount = LIVE_QUEST_REWARD_SLOTS.filter((r) =>
    isComplete(liveQuestAssetKind(r), assets[r.id] || {}, themeSettingsFor(r.id).lockAppearance)
  ).length;
  const trackDoneCount = LIVE_QUEST_REWARD_SLOTS.filter(
    (r) =>
      r.track === track &&
      isComplete(liveQuestAssetKind(r), assets[r.id] || {}, themeSettingsFor(r.id).lockAppearance)
  ).length;

  const describeReward = (reward: LiveQuestRewardSlot): UploadDescription => ({
    productKey: reward.productKey,
    name: `Live Quest ${reward.track === 'basic' ? 'Basic' : 'Plus'} LV ${reward.level}: ${reward.title}`,
    description: reward.subtitle,
    order: reward.level * 2 + (reward.track === 'plus' ? 1 : 0),
    tags: ['live_quest', LIVE_QUEST_SEASON_ID.replace(/-/g, '_'), reward.track, reward.rewardType],
    metadata: {
      LiveQuestRewardId: reward.id,
      LiveQuestSeasonId: LIVE_QUEST_SEASON_ID,
      Track: reward.track,
      Level: reward.level,
      RewardType: reward.rewardType,
      Amount: reward.amount,
    },
  });

  const heroDescription: UploadDescription = {
    productKey: liveQuestProductKey(LIVE_QUEST_HERO_ID),
    name: `Live Quest ${LIVE_QUEST_SEASON_NAME} hero crest`,
    description: 'Header artwork shown on every Live Quest tab',
    order: 0,
    tags: ['live_quest', LIVE_QUEST_SEASON_ID.replace(/-/g, '_'), 'hero'],
    metadata: { LiveQuestRewardId: LIVE_QUEST_HERO_ID, LiveQuestSeasonId: LIVE_QUEST_SEASON_ID, RewardType: 'hero' },
  };

  const describeLeaderboardPrize = (prize: LiveQuestLeaderboardPrizeAsset): UploadDescription => ({
    productKey: liveQuestProductKey(prize.id),
    name: `Live Quest ${LIVE_QUEST_SEASON_NAME} leaderboard: ${prize.title}`,
    description: `${prize.tokenLabel} · Rank ${prize.rank} leaderboard prize card artwork`,
    order: 100 + prize.rank,
    tags: ['live_quest', LIVE_QUEST_SEASON_ID.replace(/-/g, '_'), 'leaderboard', `rank_${prize.rank}`],
    metadata: {
      LiveQuestRewardId: prize.id,
      LiveQuestSeasonId: LIVE_QUEST_SEASON_ID,
      RewardType: 'leaderboardPrize',
      Rank: prize.rank,
      TokenLabel: prize.tokenLabel,
    },
  });

  const describeUiAsset = (asset: LiveQuestUiAsset): UploadDescription => ({
    productKey: liveQuestProductKey(asset.id),
    name: `Live Quest: ${asset.title}`,
    description: asset.description,
    order: 200 + LIVE_QUEST_UI_ASSETS.findIndex((item) => item.id === asset.id),
    tags: ['live_quest', LIVE_QUEST_SEASON_ID.replace(/-/g, '_'), 'ui_asset', asset.group.toLowerCase().replace(/[^a-z0-9]+/g, '_')],
    metadata: {
      LiveQuestRewardId: asset.id, LiveQuestSeasonId: LIVE_QUEST_SEASON_ID,
      RewardType: 'uiAsset', AssetGroup: asset.group,
      ...(asset.id === 'onboarding_hero' ? { OnboardingContent: parseOnboardingContent(contentDraft) } : {}),
    },
  });

  const saveOnboardingContent = async () => {
    const headers = authHeaders();
    if (!headers) return;
    setUploadingKey('onboarding_content:copy');
    try {
      const hero = LIVE_QUEST_UI_ASSETS.find((asset) => asset.id === 'onboarding_hero');
      if (!hero || !assets.onboarding_hero?.image) throw new Error('Upload the onboarding hero artwork before saving its copy.');
      await saveProduct(headers, 'onboarding_hero', 'hero', {
        productKey: liveQuestProductKey('onboarding_hero'),
        name: `Live Quest: ${hero.title}`,
        description: hero.description,
        order: 200 + LIVE_QUEST_UI_ASSETS.findIndex((asset) => asset.id === 'onboarding_hero'),
        tags: ['live_quest', LIVE_QUEST_SEASON_ID.replace(/-/g, '_'), 'onboarding'],
        metadata: {
          LiveQuestRewardId: 'onboarding_hero',
          LiveQuestSeasonId: LIVE_QUEST_SEASON_ID,
          RewardType: 'uiAsset',
          OnboardingContent: parseOnboardingContent(contentDraft),
        },
      }, Object.values(assets.onboarding_hero)
        .filter((media): media is SavedAsset => !!media)
        .map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height })));
      await loadAssets();
      toast.success('Onboarding copy saved. App updates within a minute.');
    } catch (error) {
      toast.error((error as Error).message || 'Could not save onboarding copy');
    } finally {
      setUploadingKey('');
    }
  };

  const uploadRewardFile = (reward: LiveQuestRewardSlot, slot: LiveQuestMediaSlot, file: File) =>
    handleUpload(reward.id, liveQuestAssetKind(reward), slot, file, describeReward(reward));

  const removeRewardFile = (reward: LiveQuestRewardSlot, slot: LiveQuestMediaSlot) =>
    handleRemove(reward.id, liveQuestAssetKind(reward), slot, describeReward(reward));

  /** Item rewards whose product still has the old LIVE_QUEST utility type: they cannot be given to users yet. */
  const needsItemType = LIVE_QUEST_REWARD_SLOTS.filter((reward) => {
    const kind = liveQuestAssetKind(reward);
    const wanted = liveQuestProductClass(kind);
    const current = productClasses[reward.id];
    return (
      wanted.Type !== 'utility' &&
      isComplete(kind, assets[reward.id] || {}, themeSettingsFor(reward.id).lockAppearance) &&
      (current?.Type !== wanted.Type || current?.CategoryKey !== wanted.CategoryKey)
    );
  });

  const applyItemTypes = async () => {
    const headers = authHeaders();
    if (!headers) return;
    setUploadingKey('convert');
    try {
      for (const reward of needsItemType) {
        const media = Object.values(assets[reward.id] || {})
          .filter((item): item is SavedAsset => !!item)
          .map(({ Type, Url, MimeType, Width, Height }) => ({ Type, Url, MimeType, Width, Height }));
        await commitMedia(headers, reward.id, liveQuestAssetKind(reward), describeReward(reward), media);
      }
      toast.success(`${needsItemType.length} reward${needsItemType.length === 1 ? '' : 's'} updated`);
    } catch (error) {
      toast.error((error as Error).message || 'Could not update the reward types');
    } finally {
      setUploadingKey('');
    }
  };

  const uploadHeroFile = (slot: LiveQuestMediaSlot, file: File) =>
    handleUpload(LIVE_QUEST_HERO_ID, 'hero', slot, file, heroDescription);

  const removeHeroFile = (slot: LiveQuestMediaSlot) =>
    handleRemove(LIVE_QUEST_HERO_ID, 'hero', slot, heroDescription);

  const uploadLeaderboardPrizeFile = (
    prize: LiveQuestLeaderboardPrizeAsset,
    slot: LiveQuestMediaSlot,
    file: File
  ) => handleUpload(prize.id, 'leaderboard_prize', slot, file, describeLeaderboardPrize(prize));

  const removeLeaderboardPrizeFile = (prize: LiveQuestLeaderboardPrizeAsset, slot: LiveQuestMediaSlot) =>
    handleRemove(prize.id, 'leaderboard_prize', slot, describeLeaderboardPrize(prize));

  const uploadUiAssetFile = (asset: LiveQuestUiAsset, slot: LiveQuestMediaSlot, file: File) =>
    handleUpload(asset.id, asset.assetKind || 'hero', slot, file, describeUiAsset(asset));

  const removeUiAssetFile = (asset: LiveQuestUiAsset, slot: LiveQuestMediaSlot) =>
    handleRemove(asset.id, asset.assetKind || 'hero', slot, describeUiAsset(asset));

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-950 via-[#11152d] to-slate-900'>
      <Navbar />
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8'>
          <div>
            <div className='inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-400/10 px-3 py-1 text-xs font-semibold text-fuchsia-200 mb-4'>
              {LIVE_QUEST_SEASON_NAME} · 100 reward stages + separate badge/frame assets
            </div>
            <h1 className='text-3xl sm:text-4xl font-bold text-white'>Live Quest Assets</h1>
            <p className='mt-2 max-w-2xl text-sm sm:text-base text-gray-400'>
              Upload the artwork for the Basic and Plus reward tracks and the season hero crest. Each card lists exactly what
              that reward needs. Replacing a file keeps the reward ID and payout unchanged.
            </p>
          </div>
          <div className='grid grid-cols-2 gap-3 min-w-[280px]'>
            <div className='rounded-2xl border border-white/10 bg-white/5 p-4'>
              <div className='text-2xl font-bold text-white'>{doneCount}/{LIVE_QUEST_REWARD_SLOTS.length}</div>
              <div className='text-xs text-gray-500'>Rewards complete</div>
            </div>
            <div className='rounded-2xl border border-white/10 bg-white/5 p-4'>
              <div className='text-2xl font-bold text-white'>
                {trackDoneCount}/{LIVE_QUEST_REWARD_SLOTS.filter((reward) => reward.track === track).length}
              </div>
              <div className='text-xs text-gray-500'>{track === 'basic' ? 'Basic' : 'Plus'} track</div>
            </div>
          </div>
        </div>

        <HeroCard
          have={assets[LIVE_QUEST_HERO_ID] || {}}
          uploadingKey={uploadingKey}
          onUpload={uploadHeroFile}
          onRemove={removeHeroFile}
        />

        <OnboardingCopyEditor
          value={contentDraft}
          disabled={!!uploadingKey}
          onChange={setContentDraft}
          onSave={saveOnboardingContent}
        />

        <section className='mb-7'>
          <div className='mb-3'>
            <h2 className='text-xl font-semibold text-white'>Leaderboard prize images</h2>
            <p className='mt-1 text-sm text-gray-400'>Artwork displayed in the Champion, Runner-Up, and Top 3 cards.</p>
          </div>
          <div className='grid grid-cols-1 gap-5 md:grid-cols-3'>
            {LIVE_QUEST_LEADERBOARD_PRIZE_ASSETS.map((prize) => (
              <LeaderboardPrizeCard
                key={prize.id}
                prize={prize}
                have={assets[prize.id] || {}}
                uploadingKey={uploadingKey}
                onUpload={(slot, file) => uploadLeaderboardPrizeFile(prize, slot, file)}
                onRemove={(slot) => removeLeaderboardPrizeFile(prize, slot)}
              />
            ))}
          </div>
        </section>

        <section className='mb-7'>
          <div className='mb-3'>
            <h2 className='text-xl font-semibold text-white'>Flow and identity artwork</h2>
            <p className='mt-1 text-sm text-gray-400'>
              Permanent ranks, XP Boost, onboarding, celebrations and shared artwork. Missing files remain visibly missing in the app.
            </p>
          </div>
          {Array.from(new Set(LIVE_QUEST_UI_ASSETS.map((asset) => asset.group))).map((group) => (
            <div key={group} className='mb-6'>
              <h3 className='mb-3 text-sm font-semibold uppercase tracking-wide text-fuchsia-200'>{group}</h3>
              <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                {LIVE_QUEST_UI_ASSETS.filter((asset) => asset.group === group).map((asset) => (
                  <UiAssetCard
                    key={asset.id}
                    asset={asset}
                    have={assets[asset.id] || {}}
                    uploadingKey={uploadingKey}
                    onUpload={(slot, file) => uploadUiAssetFile(asset, slot, file)}
                    onRemove={(slot) => removeUiAssetFile(asset, slot)}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>

        {savedPackId && assets[savedPackId] && (
          <div className='mb-7 flex items-center justify-between gap-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm text-emerald-100'>
            <span>Sticker pack saved and added to Live Quest. The completed pack is shown below.</span>
            <button type='button' onClick={() => setQuery('')} className='shrink-0 font-semibold underline'>
              Show all rewards
            </button>
          </div>
        )}

        {needsItemType.length > 0 && (
          <div className='mb-7 flex flex-col gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100 sm:flex-row sm:items-center sm:justify-between'>
            <span>
              {needsItemType.length} reward{needsItemType.length === 1 ? '' : 's'} with artwork still use the old
              type, so users cannot be given them yet ({needsItemType.map((reward) => reward.id).join(', ')}).
            </span>
            <button
              type='button'
              onClick={applyItemTypes}
              disabled={!!uploadingKey}
              className='shrink-0 rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50'
            >
              {uploadingKey === 'convert' ? 'Updating…' : 'Update reward types'}
            </button>
          </div>
        )}

        <div className='sticky top-[81px] z-30 rounded-2xl border border-white/10 bg-slate-950/90 backdrop-blur-xl p-4 mb-7 shadow-2xl'>
          <div className='flex flex-col xl:flex-row gap-4 xl:items-center xl:justify-between'>
            <div className='inline-flex rounded-xl bg-white/5 p-1 self-start'>
              {(['basic', 'plus'] as LiveQuestTrack[]).map((item) => (
                <button
                  key={item}
                  onClick={() => setTrack(item)}
                  className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${
                    track === item
                      ? item === 'basic'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                        : 'bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {item === 'basic' ? 'Basic Track' : 'Plus Track'}
                </button>
              ))}
            </div>

            <div className='flex flex-col sm:flex-row gap-3 sm:items-center'>
              <input
                type='search'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder='Search title, ID, or level'
                className='w-full sm:w-64 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-gray-600 outline-none focus:border-fuchsia-400/50'
              />
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
                className='rounded-xl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-fuchsia-400/50'
              >
                <option value='all'>All slots</option>
                <option value='missing'>Incomplete</option>
                <option value='uploaded'>Complete</option>
                <option value='cosmetics'>Cosmetics only</option>
              </select>
              <button
                onClick={loadAssets}
                disabled={loading}
                className='rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-gray-200 hover:bg-white/10 disabled:opacity-50'
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {loading && Object.keys(assets).length === 0 ? (
          <div className='rounded-2xl border border-white/10 bg-white/5 py-20 text-center text-sm text-gray-400'>Loading reward artwork…</div>
        ) : visibleRewards.length === 0 ? (
          <div className='rounded-2xl border border-white/10 bg-white/5 py-20 text-center text-sm text-gray-400'>No reward slots match this filter.</div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'>
            {visibleRewards.map((reward) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                track={track}
                have={assets[reward.id] || {}}
                uploadingKey={uploadingKey}
                onUpload={(slot, file) => uploadRewardFile(reward, slot, file)}
                onRemove={(slot) => removeRewardFile(reward, slot)}
                themeSettings={themeSettingsFor(reward.id)}
                onChangeThemeSettings={(settings) =>
                  setThemeDrafts((current) => ({ ...current, [reward.id]: settings }))
                }
                onCleanStale={() => cleanStale(reward)}
                onSaveThemeSettings={(settings) => saveThemeSettings(reward.id, describeReward(reward), settings)}
                onSyncStickerPack={() => syncStickerPack(reward.id)}
                syncingStickerPack={syncingPack === reward.id}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

type UploadButtonProps = {
  slot: LiveQuestMediaSlot;
  have?: SavedAsset;
  busy: boolean;
  disabled: boolean;
  gradient: string;
  onFile: (file: File) => void;
  onRemove: () => void;
};

const SlotRow: React.FC<UploadButtonProps> = ({ slot, have, busy, disabled, gradient, onFile, onRemove }) => (
  <div className='rounded-xl border border-white/10 bg-black/20 p-3'>
    <div className='flex items-center justify-between gap-2'>
      <div className='min-w-0'>
        <div className='flex items-center gap-2'>
          <span className={`h-2 w-2 shrink-0 rounded-full ${have ? 'bg-emerald-400' : slot.required ? 'bg-amber-400' : 'bg-gray-600'}`} />
          <span className='truncate text-xs font-semibold text-white'>{slot.label}</span>
          <span className='text-[10px] text-gray-500'>{slot.required ? 'required' : 'optional'}</span>
        </div>
        <p className='mt-0.5 truncate text-[10px] text-gray-500'>{slot.hint}</p>
      </div>
      <div className='flex shrink-0 items-center gap-1.5'>
      {have && (
        <button
          type='button'
          onClick={onRemove}
          disabled={disabled}
          className='rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-semibold text-gray-300 hover:bg-white/10 disabled:opacity-40'
        >
          Remove
        </button>
      )}
      <label
        className={`shrink-0 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition ${
          disabled ? 'cursor-not-allowed bg-white/10 text-gray-500' : `cursor-pointer text-white hover:opacity-90 ${gradient}`
        }`}
      >
        {busy ? 'Working…' : have ? 'Update' : 'Upload'}
        <input
          type='file'
          accept={slot.accept}
          className='hidden'
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) onFile(file);
          }}
        />
      </label>
      </div>
    </div>
    {have && (
      <a href={have.Url} target='_blank' rel='noreferrer' className='mt-2 block truncate text-[10px] text-cyan-300/80 hover:underline'>
        {have.Url.split('/').pop()}
        {have.Width && have.Height ? ` · ${have.Width}×${have.Height}` : ''}
      </a>
    )}
  </div>
);

const PreviewBox: React.FC<{ have: RewardAssets; label: string; badge: React.ReactNode }> = ({ have, label, badge }) => {
  const preview = previewOf(have);
  return (
    <div className='relative aspect-[4/3] bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.22),_rgba(15,23,42,0.3)_55%,_rgba(2,6,23,0.8))] p-5 flex items-center justify-center'>
      {badge}
      {preview ? (
        <img
          src={`${preview.Url}?v=${encodeURIComponent(preview.updatedAt || '')}`}
          alt={label}
          className='h-full w-full object-contain drop-shadow-2xl'
        />
      ) : (
        <div className='text-center'>
          <div className='mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-dashed border-white/20 text-2xl text-gray-600'>✦</div>
          <span className='text-xs text-gray-600'>No picture yet</span>
        </div>
      )}
    </div>
  );
};

const RewardCard: React.FC<{
  reward: LiveQuestRewardSlot;
  track: LiveQuestTrack;
  have: RewardAssets;
  uploadingKey: string;
  onUpload: (slot: LiveQuestMediaSlot, file: File) => void;
  onRemove: (slot: LiveQuestMediaSlot) => void;
  themeSettings: ChatThemeSettings;
  onChangeThemeSettings: (settings: ChatThemeSettings) => void;
  onSaveThemeSettings: (settings: ChatThemeSettings) => void;
  onCleanStale: () => void;
  onSyncStickerPack: () => void;
  syncingStickerPack: boolean;
}> = ({
  reward,
  track,
  have,
  uploadingKey,
  onUpload,
  onRemove,
  themeSettings,
  onChangeThemeSettings,
  onSaveThemeSettings,
  onCleanStale,
  onSyncStickerPack,
  syncingStickerPack,
}) => {
  const kind = liveQuestAssetKind(reward);
  const profile = liveQuestAssetProfile(kind);
  const complete = isComplete(kind, have, themeSettings.lockAppearance);
  const allowed = allowedMediaTypes(kind);
  const staleTypes = Object.keys(have).filter((type) => !allowed.has(type));
  const gradient = track === 'basic' ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gradient-to-r from-fuchsia-500 to-violet-600';

  return (
    <article className='flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-xl transition hover:border-white/20'>
      <PreviewBox
        have={have}
        label={`${reward.title} reward`}
        badge={
          <div className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold ${track === 'basic' ? 'bg-cyan-400/15 text-cyan-200' : 'bg-fuchsia-400/15 text-fuchsia-200'}`}>
            LV {reward.level}
          </div>
        }
      />
      <div className='flex flex-1 flex-col p-4'>
        <div className='mb-1 flex items-start justify-between gap-2'>
          <h2 className='text-sm font-semibold leading-5 text-white'>{reward.title}</h2>
          <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${complete ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        </div>
        <p className='truncate text-xs text-gray-500'>{reward.subtitle}</p>
        <div className='mt-2 flex items-center justify-between gap-2'>
          <span className='rounded-full bg-white/5 px-2 py-1 text-[10px] text-gray-400'>{profile.title}</span>
          <span className='font-mono text-[10px] text-gray-600'>{reward.id}</span>
        </div>

        <ul className='mt-3 space-y-1 text-[11px] leading-4 text-gray-400'>
          {profile.requirements.map((line) => (
            <li key={line}>• {line}</li>
          ))}
        </ul>
        {profile.contact && <p className='mt-2 text-[11px] text-fuchsia-200/80'>{profile.contact}</p>}
        {profile.managePath && (
          <Link
            to={`${profile.managePath}?liveQuest=${reward.id}`}
            className='mt-1 block text-[11px] font-semibold text-cyan-300 hover:underline'
          >
            Upload this pack (Live Quest only, free, not in the Shop) →
          </Link>
        )}
        {reward.rewardType === 'stickerPack' && (
          <button type='button' onClick={onSyncStickerPack} disabled={syncingStickerPack}
            className='mt-2 text-left text-[11px] font-semibold text-emerald-300 hover:underline disabled:opacity-40'>
            {syncingStickerPack ? 'Updating chat stickers…' : 'Update chat stickers from saved pack'}
          </button>
        )}

        {staleTypes.length > 0 && (
          <div className='mt-3 rounded-lg border border-amber-400/30 bg-amber-400/10 p-2 text-[11px] text-amber-100'>
            Artwork from a previous reward is still attached ({staleTypes.join(', ')}). It is ignored by the app now, and
            removed on your next upload.
            <button
              type='button'
              onClick={onCleanStale}
              disabled={!!uploadingKey}
              className='ml-2 font-semibold underline disabled:opacity-40'
            >
              Remove it now
            </button>
          </div>
        )}

        <div className='mt-4 space-y-2'>
          {[...profile.slots, ...(kind === 'chat_theme' && !themeSettings.lockAppearance ? CHAT_THEME_DARK_SLOTS : [])].map(
            (slot) => (
              <SlotRow
                key={slot.type}
                slot={slot}
                have={have[slot.type]}
                busy={uploadingKey === `${reward.id}:${slot.type}`}
                disabled={!!uploadingKey}
                gradient={gradient}
                onFile={(file) => onUpload(slot, file)}
                onRemove={() => onRemove(slot)}
              />
            )
          )}
        </div>
        {kind === 'chat_theme' && (
          <ThemeSettingsPanel
            settings={themeSettings}
            busy={uploadingKey === `${reward.id}:settings`}
            disabled={!!uploadingKey}
            gradient={gradient}
            onChange={onChangeThemeSettings}
            onSave={onSaveThemeSettings}
          />
        )}
      </div>
    </article>
  );
};

/** Bubble colors and the light/dark lock for a chat theme. */
export const ThemeSettingsPanel: React.FC<{
  settings: ChatThemeSettings;
  busy: boolean;
  disabled: boolean;
  gradient: string;
  onChange: (settings: ChatThemeSettings) => void;
  onSave: (settings: ChatThemeSettings) => void;
}> = ({ settings, busy, disabled, gradient, onChange, onSave }) => {
  const swatch = (value: string) => (
    <span
      className='inline-block h-4 w-4 rounded border border-white/20'
      style={{ backgroundColor: isHexColor(value) ? value : 'transparent' }}
    />
  );
  const input = (label: string, value: string, onChange: (value: string) => void) => (
    <label className='block'>
      <span className='mb-1 flex items-center gap-2 text-[11px] text-gray-400'>
        {swatch(value)}
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder='#F0DBE0'
        maxLength={7}
        className='w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white outline-none focus:border-fuchsia-400/50'
      />
    </label>
  );
  return (
    <div className='mt-4 rounded-xl border border-white/10 bg-black/20 p-3'>
      <label className='flex items-start gap-2 text-xs text-white'>
        <input
          type='checkbox'
          checked={settings.lockAppearance}
          onChange={(event) => onChange({ ...settings, lockAppearance: event.target.checked })}
          className='mt-0.5'
        />
        <span>
          <span className='font-semibold'>Lock the look in light and dark mode</span>
          <span className='block text-[10px] text-gray-500'>
            On: the chat looks exactly as designed whatever the phone is set to. Off: upload dark-mode files and a dark
            bubble color too.
          </span>
        </span>
      </label>
      <div className='mt-3 grid grid-cols-2 gap-2'>
        {input(settings.lockAppearance ? 'Sender color' : 'Sender color (light)', settings.senderLight, (value) =>
          onChange({ ...settings, senderLight: value })
        )}
        {input(settings.lockAppearance ? 'Receiver color' : 'Receiver color (light)', settings.receiverLight, (value) =>
          onChange({ ...settings, receiverLight: value })
        )}
        {!settings.lockAppearance &&
          input('Sender color (dark)', settings.senderDark, (value) => onChange({ ...settings, senderDark: value }))}
        {!settings.lockAppearance &&
          input('Receiver color (dark)', settings.receiverDark, (value) => onChange({ ...settings, receiverDark: value }))}
      </div>
      <button
        type='button'
        onClick={() => onSave(settings)}
        disabled={disabled}
        className={`mt-3 w-full rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40 ${gradient}`}
      >
        {busy ? 'Saving…' : 'Save theme settings'}
      </button>
    </div>
  );
};

const LeaderboardPrizeCard: React.FC<{
  prize: LiveQuestLeaderboardPrizeAsset;
  have: RewardAssets;
  uploadingKey: string;
  onUpload: (slot: LiveQuestMediaSlot, file: File) => void;
  onRemove: (slot: LiveQuestMediaSlot) => void;
}> = ({ prize, have, uploadingKey, onUpload, onRemove }) => {
  const profile = liveQuestAssetProfile('leaderboard_prize');
  return (
    <article className='overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-xl'>
      <PreviewBox
        have={have}
        label={`${prize.title} leaderboard prize`}
        badge={
          <div className='absolute left-3 top-3 rounded-full bg-fuchsia-400/15 px-2.5 py-1 text-[10px] font-bold text-fuchsia-200'>
            RANK {prize.rank}
          </div>
        }
      />
      <div className='p-4'>
        <h3 className='text-sm font-semibold text-white'>{prize.title}</h3>
        <p className='mt-1 text-xs text-gray-400'>{prize.tokenLabel}</p>
        <ul className='mt-3 space-y-1 text-[11px] leading-4 text-gray-400'>
          {profile.requirements.map((line) => (
            <li key={line}>• {line}</li>
          ))}
        </ul>
        {profile.contact && <p className='mt-2 text-[11px] text-fuchsia-200/80'>{profile.contact}</p>}
        <div className='mt-4'>
          {profile.slots.map((slot) => (
            <SlotRow
              key={slot.type}
              slot={slot}
              have={have[slot.type]}
              busy={uploadingKey === `${prize.id}:${slot.type}`}
              disabled={!!uploadingKey}
              gradient='bg-gradient-to-r from-fuchsia-500 to-violet-600'
              onFile={(file) => onUpload(slot, file)}
              onRemove={() => onRemove(slot)}
            />
          ))}
        </div>
      </div>
    </article>
  );
};

const UiAssetCard: React.FC<{
  asset: LiveQuestUiAsset;
  have: RewardAssets;
  uploadingKey: string;
  onUpload: (slot: LiveQuestMediaSlot, file: File) => void;
  onRemove: (slot: LiveQuestMediaSlot) => void;
}> = ({ asset, have, uploadingKey, onUpload, onRemove }) => {
  const profile = liveQuestAssetProfile(asset.assetKind || 'hero');
  return (
    <article className='overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-xl'>
      <PreviewBox
        have={have}
        label={asset.title}
        badge={<div className='absolute left-3 top-3 rounded-full bg-cyan-400/15 px-2.5 py-1 text-[10px] font-bold text-cyan-200'>{asset.group}</div>}
      />
      <div className='p-4'>
        <h3 className='text-sm font-semibold text-white'>{asset.title}</h3>
        <p className='mt-1 min-h-10 text-xs text-gray-400'>{asset.description}</p>
        <p className='mt-2 font-mono text-[10px] text-gray-600'>{asset.id}</p>
        <div className='mt-4'>
          {profile.slots.map((slot) => (
            <SlotRow
              key={slot.type}
              slot={slot}
              have={have[slot.type]}
              busy={uploadingKey === `${asset.id}:${slot.type}`}
              disabled={!!uploadingKey}
              gradient='bg-gradient-to-r from-cyan-500 to-blue-600'
              onFile={(file) => onUpload(slot, file)}
              onRemove={() => onRemove(slot)}
            />
          ))}
        </div>
      </div>
    </article>
  );
};

const OnboardingCopyEditor: React.FC<{
  value: LiveQuestOnboardingContent;
  disabled: boolean;
  onChange: (value: LiveQuestOnboardingContent) => void;
  onSave: () => void;
}> = ({ value, disabled, onChange, onSave }) => {
  const updateCard = (group: 'ways' | 'stats', index: number, field: 'title' | 'subtitle', text: string) => {
    const next = value[group].map((card, at) => at === index ? { ...card, [field]: text } : card);
    onChange({ ...value, [group]: next });
  };
  const updateLabel = (group: 'introTiles' | 'rewardTiles', index: number, text: string) => {
    onChange({ ...value, [group]: value[group].map((label, at) => at === index ? text : label) });
  };
  const input = 'w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white';
  return <section className='mb-7 rounded-2xl border border-cyan-400/25 bg-white/5 p-5'>
    <div className='flex flex-wrap items-center justify-between gap-3'>
      <div>
        <h2 className='text-xl font-semibold text-white'>Live Quest onboarding copy</h2>
        <p className='mt-1 text-sm text-gray-400'>Edit page descriptions, activity cards, summary labels and reward names. The app reads saved copy from the backend.</p>
      </div>
      <button type='button' disabled={disabled} onClick={onSave}
        className='rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50'>Save onboarding copy</button>
    </div>
    <div className='mt-5 grid gap-4 md:grid-cols-3'>
      {([
        ['introDescription', 'Intro description'],
        ['playDescription', 'Play Your Way description'],
        ['rewardsDescription', 'Amazing Rewards description'],
        ['consentNotice', 'Join consent and inactivity notice'],
        ['inactivityTerms', 'P-Level inactivity terms'],
      ] as const).map(([key, label]) => <label key={key} className='text-xs font-semibold text-gray-300'>
        {label}
        <textarea className={`${input} mt-2 min-h-24`} maxLength={180} value={value[key]}
          onChange={(event) => onChange({ ...value, [key]: event.target.value })} />
      </label>)}
    </div>
    {(['ways', 'stats'] as const).map((group) => <div key={group} className='mt-5'>
      <h3 className='mb-2 text-sm font-semibold text-cyan-200'>{group === 'ways' ? 'Watch / Chat / Engage / Level Up' : 'Reward summary'}</h3>
      <div className='grid gap-3 md:grid-cols-4'>
        {value[group].map((card, index) => <div key={index} className='rounded-lg bg-slate-950/60 p-3'>
          <label className='text-xs text-gray-400'>Heading
            <input className={`${input} mt-1`} maxLength={80} value={card.title}
              onChange={(event) => updateCard(group, index, 'title', event.target.value)} />
          </label>
          <label className='mt-2 block text-xs text-gray-400'>Caption
            <input className={`${input} mt-1`} maxLength={100} value={card.subtitle}
              onChange={(event) => updateCard(group, index, 'subtitle', event.target.value)} />
          </label>
        </div>)}
      </div>
    </div>)}
    {(['introTiles', 'rewardTiles'] as const).map((group) => <div key={group} className='mt-5'>
      <h3 className='mb-2 text-sm font-semibold text-cyan-200'>{group === 'introTiles' ? 'Intro reward labels' : 'Rewards page labels'}</h3>
      <div className='grid gap-3 sm:grid-cols-2 md:grid-cols-4'>
        {value[group].map((label, index) => <input key={index} aria-label={`${group} ${index + 1}`}
          className={input} maxLength={80} value={label}
          onChange={(event) => updateLabel(group, index, event.target.value)} />)}
      </div>
    </div>)}
  </section>;
};

const HeroCard: React.FC<{
  have: RewardAssets;
  uploadingKey: string;
  onUpload: (slot: LiveQuestMediaSlot, file: File) => void;
  onRemove: (slot: LiveQuestMediaSlot) => void;
}> = ({ have, uploadingKey, onUpload, onRemove }) => {
  const profile = liveQuestAssetProfile('hero');
  return (
    <section className='mb-7 grid grid-cols-1 overflow-hidden rounded-2xl border border-fuchsia-400/20 bg-white/5 shadow-xl md:grid-cols-[320px_1fr]'>
      <PreviewBox
        have={have}
        label={`${LIVE_QUEST_SEASON_NAME} hero crest`}
        badge={<div className='absolute left-3 top-3 rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-bold text-amber-200'>HERO</div>}
      />
      <div className='p-5'>
        <h2 className='text-lg font-semibold text-white'>{profile.title}</h2>
        <ul className='mt-2 space-y-1 text-xs text-gray-400'>
          {profile.requirements.map((line) => (
            <li key={line}>• {line}</li>
          ))}
        </ul>
        <div className='mt-4 max-w-md'>
          {profile.slots.map((slot) => (
            <SlotRow
              key={slot.type}
              slot={slot}
              have={have[slot.type]}
              busy={uploadingKey === `${LIVE_QUEST_HERO_ID}:${slot.type}`}
              disabled={!!uploadingKey}
              gradient='bg-gradient-to-r from-amber-500 to-orange-600'
              onFile={(file) => onUpload(slot, file)}
              onRemove={() => onRemove(slot)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveQuestAssets;
